import { useEffect, useState } from "react";

import { state } from "../state/workspaceStore.js";
import { head } from "../components/common/PageHeader.jsx";
import { exportButton } from "../components/common/ExportButton.jsx";
import { searchbar } from "../components/common/SearchToolbar.jsx";
import { nodes } from "../components/common/reactHelpers.jsx";
import { esc, money } from "../utils/format.js";
import { badge } from "../components/common/StatusBadge.jsx";
import { tableViews } from "../components/tables/RecordTable.jsx";

const CUSTOMERS_API = "https://api.praachinorganics.com/api/Customers";

// Convert API status values into the labels used by the existing UI.
function getStatus(status) {
  const statuses = {
    1: "Active",
    2: "Inactive",
    3: "Blocked",
  };

  return statuses[status] || status || "";
}

// Map the API response to the fields used by the existing table.
function mapCustomer(c) {
  return {
    id: c.customerProfileId,
    userId: c.userId,
    name: c.customerName,
    customerIdentifier: c.customerIdentifier,
    email: c.email,
    phone: c.phone,
    city: c.city,
    address: c.fullAddress,
    status: getStatus(c.status),
    registration: c.registrationDate
      ? new Date(c.registrationDate).toLocaleDateString()
      : "",
    orderCount: c.orderCount ?? 0,
    orderValue: c.totalOrderValue ?? 0,
    note: c.supportNotes,
    exampleRecord: false,
  };
}

export function customers(apiCustomers = []) {
  const search = (state.query || "").trim().toLowerCase();
  const selectedStatus = state.filter || "All";

  // Universal search across all requested customer fields.
  const rows = apiCustomers.filter((c) => {
    const matchesSearch =
      !search ||
      [
        c.id,
        c.userId,
        c.name,
        c.customerIdentifier,
        c.email,
        c.phone,
        c.city,
        c.address,
        c.status,
        c.registration,
        c.note,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(search),
      );

    const matchesStatus =
      selectedStatus === "All" ||
      !selectedStatus ||
      c.status.toLowerCase() === String(selectedStatus).toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const statuses = [
    ...new Set(apiCustomers.map((c) => c.status).filter(Boolean)),
  ];

  return (
    <>
      {head(
        "RELATIONSHIPS",
        "Customers",
        "One customer record for list, details, editing and exports.",
        exportButton("customers"),
      )}

      <div className={"card"}>
        {searchbar("Search ID, name, email, phone, city or notes…", [
          "All",
          ...statuses,
        ])}

        <div className={"table-wrap"}>
          <table className={"customer-table"}>
            <thead>
              <tr>
                <th>{"ID / Name"}</th>
                <th>{"Email / Phone"}</th>
                <th>{"Location / Address"}</th>
                <th>{"Status / Registered"}</th>
                <th>{"Orders / Value"}</th>
                <th>{"Support notes"}</th>
                <th>{"Actions"}</th>
              </tr>
            </thead>

            <tbody>
              {nodes(
                rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <b>{esc(r.name)}</b>
                      <small>{esc(r.customerIdentifier || r.id)}</small>
                    </td>

                    <td>
                      {esc(r.email || "—")}
                      <small>{esc(r.phone || "— No phone")}</small>
                    </td>

                    <td>
                      {esc(r.city || "—")}
                      <small className={"table-long"}>
                        {esc(r.address || "— No address")}
                      </small>
                    </td>

                    <td>
                      {r.status ? badge(r.status) : "—"}
                      <small>{esc(r.registration || "— Not recorded")}</small>
                    </td>

                    <td>
                      {r.orderCount}
                      <small>
                        {money(r.orderValue)}
                        {" · non-cancelled"}
                      </small>
                    </td>

                    <td className={"table-long"}>{esc(r.note || "—")}</td>

                    <td>
                      <button
                        className={"text-btn"}
                        data-nav={"customer"}
                        data-id={r.id}
                      >
                        {"View"}
                      </button>

                      <button
                        className={"text-btn"}
                        data-nav={"customer-edit"}
                        data-id={r.id}
                      >
                        {"Edit"}
                      </button>
                    </td>
                  </tr>
                )),
              ) || (
                  <tr>
                    <td colSpan={"7"} className={"empty"}>
                      {"No matching customers. Clear or change the filters."}
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default function CustomersPage({ recordId } = {}) {
  const [apiCustomers, setApiCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    async function loadCustomers() {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(CUSTOMERS_API, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load customers (${response.status})`);
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
          throw new Error("The customers API did not return an array.");
        }

        setApiCustomers(data.map(mapCustomer));
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading customers:", err);
          setError(err.message || "Unable to load customers.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadCustomers();

    return () => controller.abort();
  }, []);

  if (loading) {
    return (
      <>
        {head(
          "RELATIONSHIPS",
          "Customers",
          "One customer record for list, details, editing and exports.",
          exportButton("customers"),
        )}
        <div className={"card"}>
          <div className={"table-wrap"}>{"Loading customers..."}</div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {head(
          "RELATIONSHIPS",
          "Customers",
          "One customer record for list, details, editing and exports.",
          exportButton("customers"),
        )}
        <div className={"card"}>
          <div className={"table-wrap"}>{esc(error)}</div>
        </div>
      </>
    );
  }

  return tableViews(customers(apiCustomers));
}
